import { useCallback, useEffect, useReducer, useRef } from "react";
import { useApi } from "../api/context";
import { ApiError } from "../api/types";
import { initialInvoiceState, invoiceReducer, isFinal } from "../lib/status";
import type { InvoiceStatus } from "../lib/types";

/** Poll interval while the SSE stream is down, and a slow safety poll while it is up. */
export const POLL_FALLBACK_MS = 4000;
export const POLL_SAFETY_MS = 20000;
/** Background tabs poll less often; the page catches up the moment it is visible again. */
export const POLL_HIDDEN_MS = 15000;
/** An expired invoice can still be paid late (the server watches it for 24 h): check now and then. */
export const POLL_EXPIRED_MS = 30000;
/** Reopening a stream the browser gave up on: 3 s, 6 s, 12 s … capped. */
export const RESUBSCRIBE_MIN_MS = 3000;
export const RESUBSCRIBE_MAX_MS = 30000;

/** Statuses after which nothing can change any more. `expired` is not one of them (late payments). */
export function isSettledForGood(status: InvoiceStatus): boolean {
  return isFinal(status) && status !== "expired";
}

export function nextPollDelay(opts: { transport: "connecting" | "live" | "polling"; hidden: boolean; expired: boolean }): number {
  if (opts.expired) return POLL_EXPIRED_MS;
  if (opts.hidden) return opts.transport === "live" ? POLL_SAFETY_MS : POLL_HIDDEN_MS;
  return opts.transport === "live" ? POLL_SAFETY_MS : POLL_FALLBACK_MS;
}

export function resubscribeDelay(attempt: number): number {
  return Math.min(RESUBSCRIBE_MAX_MS, RESUBSCRIBE_MIN_MS * 2 ** Math.max(0, attempt));
}

/**
 * Live invoice: initial GET, then SSE for changes, with polling whenever the stream is not open.
 *
 * Robust to real life: a stream the browser gave up on is reopened with backoff; going offline
 * switches to polling and coming back online (or waking a sleeping tab) re-fetches at once; a slow
 * response that lands after a newer one is ignored. Stops once the invoice can no longer change.
 */
export function useInvoice(id: string) {
  const api = useApi();
  const [state, dispatch] = useReducer(invoiceReducer, initialInvoiceState);
  const status = state.invoice?.status ?? null;
  const done = status !== null && isSettledForGood(status);
  const expired = status === "expired";
  const transportRef = useRef(state.transport);
  transportRef.current = state.transport;
  const seq = useRef(0);
  const applied = useRef(0);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      const mine = ++seq.current;
      try {
        const invoice = await api.getInvoice(id, signal);
        if (mine < applied.current) return; // a newer response already landed
        applied.current = mine;
        dispatch({ type: "loaded", invoice, at: Date.now() });
      } catch (err) {
        if ((err as Error).name === "AbortError") return;
        dispatch({ type: "load_failed", error: err instanceof ApiError ? err.code : "network" });
      }
    },
    [api, id],
  );

  // Initial load.
  useEffect(() => {
    const ctrl = new AbortController();
    void load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  const loaded = state.invoice !== null;

  // Stream (not for expired invoices: a slow poll is enough there).
  useEffect(() => {
    if (!loaded || done || expired) return;
    let unsubscribe: () => void = () => {};
    let retryTimer = 0;
    let attempt = 0;
    let closed = false;
    let stopped = false;

    const open = () => {
      if (stopped) return;
      closed = false;
      unsubscribe = api.subscribe(id, {
        onOpen: () => {
          attempt = 0;
          // The server sends the current status as the first event, which triggers a full load.
          dispatch({ type: "transport", transport: "live" });
        },
        onError: (isClosed) => {
          dispatch({ type: "transport", transport: "polling" });
          if (isClosed && !closed) {
            closed = true;
            unsubscribe();
            window.clearTimeout(retryTimer);
            retryTimer = window.setTimeout(open, resubscribeDelay(attempt++));
          }
        },
        onEvent: (patch) => {
          dispatch({ type: "event", patch, at: Date.now() });
          // The event may be partial; fetch the full invoice so amounts and evidence are exact.
          void load();
        },
      });
    };

    const reopenNow = () => {
      if (!closed) return;
      window.clearTimeout(retryTimer);
      attempt = 0;
      open();
    };
    const onOnline = () => {
      void load();
      reopenNow();
    };
    const onOffline = () => dispatch({ type: "transport", transport: "polling" });
    const onVisible = () => {
      if (document.visibilityState === "visible") reopenNow();
    };

    open();
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      stopped = true;
      unsubscribe();
      window.clearTimeout(retryTimer);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [api, id, loaded, done, expired, load]);

  // Polling: the fallback while the stream is down, a safety net while it is up.
  useEffect(() => {
    if (!loaded || done) return;
    let timer = 0;
    const tick = () => {
      const delay = nextPollDelay({ transport: transportRef.current, hidden: document.visibilityState === "hidden", expired });
      timer = window.setTimeout(async () => {
        if (navigator.onLine !== false) await load();
        tick();
      }, delay);
    };
    tick();
    // A tab waking from sleep may have missed everything: fetch at once, then resume the rhythm.
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      window.clearTimeout(timer);
      void load();
      tick();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [loaded, done, expired, load]);

  return { ...state, reload: load };
}
